import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ShieldCheck, KeyRound, UserPlus, Power, RotateCcw, Undo2,
    Loader2, Crown, LogOut, Users, History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

export default function SuperAdminPage() {
    const [authorized, setAuthorized] = useState(null); // null = checking
    const [me, setMe] = useState(null);
    const [admins, setAdmins] = useState([]);
    const [batches, setBatches] = useState([]);
    const [busy, setBusy] = useState(false);

    // create admin form
    const [cEmail, setCEmail] = useState('');
    const [cName, setCName] = useState('');
    const [cPass, setCPass] = useState('');
    const [cSuper, setCSuper] = useState(false);

    // change own password
    const [curPw, setCurPw] = useState('');
    const [newPw, setNewPw] = useState('');

    // reset-password dialog
    const [resetTarget, setResetTarget] = useState(null);
    const [resetPw, setResetPw] = useState('');

    const loadAll = async () => {
        try {
            const [a, b] = await Promise.all([
                axios.get(`${API}/control/admins`, cfg),
                axios.get(`${API}/control/price-batches`, cfg),
            ]);
            setAdmins(a.data || []);
            setBatches(b.data || []);
        } catch (e) { /* ignore */ }
    };

    useEffect(() => {
        axios.get(`${API}/control/verify`, cfg)
            .then(r => { setMe(r.data); setAuthorized(true); loadAll(); })
            .catch(() => setAuthorized(false));
    }, []);

    if (authorized === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950">
                <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
            </div>
        );
    }
    if (authorized === false) return <Navigate to="/" replace />;

    const createAdmin = async () => {
        if (!cEmail || !cName || cPass.length < 8) {
            toast.error('Email, name and a password of 8+ characters are required');
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/control/admins`, { email: cEmail, name: cName, password: cPass, is_super_admin: cSuper }, cfg);
            toast.success('Admin account created');
            setCEmail(''); setCName(''); setCPass(''); setCSuper(false);
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not create admin');
        } finally { setBusy(false); }
    };

    const patchAdmin = async (u, body, okMsg) => {
        setBusy(true);
        try {
            await axios.patch(`${API}/control/admins/${u.user_id}`, body, cfg);
            toast.success(okMsg);
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Update failed');
        } finally { setBusy(false); }
    };

    const toggleActive = (u) => patchAdmin(u, { is_active: !u.is_active }, u.is_active ? 'Account disabled' : 'Account enabled');
    const toggleRole = (u) => patchAdmin(u, { is_super_admin: !u.is_super_admin }, u.is_super_admin ? 'Super-admin removed' : 'Promoted to super admin');

    const doReset = async () => {
        if (resetPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/control/admins/${resetTarget.user_id}/reset-password`, { new_password: resetPw }, cfg);
            toast.success(`Password reset for ${resetTarget.email}`);
            setResetTarget(null); setResetPw('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reset failed');
        } finally { setBusy(false); }
    };

    const changeOwnPassword = async () => {
        if (newPw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/control/change-password`, { current_password: curPw, new_password: newPw }, cfg);
            toast.success('Your password was updated');
            setCurPw(''); setNewPw('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not change password');
        } finally { setBusy(false); }
    };

    const undoBatch = async (b) => {
        if (!window.confirm(`Roll back this price update (${b.updated_count} products) to the previous prices? This restores the prices as they were before this batch.`)) return;
        setBusy(true);
        try {
            const r = await axios.post(`${API}/control/price-batches/${b.batch_id}/undo`, {}, cfg);
            toast.success(`Rolled back — ${r.data.restored_count} prices restored`);
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Rollback failed');
        } finally { setBusy(false); }
    };

    const logout = async () => {
        try { await axios.post(`${API}/auth/logout`, {}, cfg); } catch (e) {}
        window.location.href = '/';
    };

    const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="control-panel">
            {/* Header */}
            <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur">
                <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                            <ShieldCheck className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold tracking-tight">Control Panel</h1>
                            <p className="text-xs text-slate-400">{me?.email}</p>
                        </div>
                    </div>
                    <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white gap-2" onClick={logout} data-testid="sa-logout">
                        <LogOut className="w-4 h-4" /> Sign out
                    </Button>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-5 py-8 space-y-10">
                {/* Admin accounts */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <Users className="w-5 h-5 text-emerald-400" />
                        <h2 className="text-base font-semibold">Admin Accounts</h2>
                    </div>

                    <div className="rounded-xl border border-slate-800 overflow-hidden">
                        <table className="w-full text-sm" data-testid="sa-admins-table">
                            <thead className="bg-slate-900/80 text-slate-400">
                                <tr className="text-left">
                                    <th className="px-4 py-2 font-medium">Email</th>
                                    <th className="px-4 py-2 font-medium">Name</th>
                                    <th className="px-4 py-2 font-medium">Role</th>
                                    <th className="px-4 py-2 font-medium">Status</th>
                                    <th className="px-4 py-2 font-medium text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {admins.map((u) => (
                                    <tr key={u.user_id} className="border-t border-slate-800" data-testid={`sa-admin-row-${u.user_id}`}>
                                        <td className="px-4 py-3">{u.email}</td>
                                        <td className="px-4 py-3 text-slate-300">{u.name}</td>
                                        <td className="px-4 py-3">
                                            {u.is_super_admin ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-300 bg-amber-400/10 rounded-full px-2 py-0.5">
                                                    <Crown className="w-3 h-3" /> Super Admin
                                                </span>
                                            ) : (
                                                <span className="text-xs text-slate-400 bg-slate-800 rounded-full px-2 py-0.5">Admin</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs font-medium ${u.is_active ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {u.is_active ? 'Active' : 'Disabled'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button variant="outline" size="sm" className="h-8 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 gap-1"
                                                    onClick={() => { setResetTarget(u); setResetPw(''); }} data-testid={`sa-reset-${u.user_id}`}>
                                                    <KeyRound className="w-3.5 h-3.5" /> Reset
                                                </Button>
                                                <Button variant="outline" size="sm" className="h-8 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 gap-1"
                                                    onClick={() => toggleRole(u)} disabled={busy} data-testid={`sa-role-${u.user_id}`}>
                                                    <Crown className="w-3.5 h-3.5" /> {u.is_super_admin ? 'Demote' : 'Promote'}
                                                </Button>
                                                <Button variant="outline" size="sm" className={`h-8 border-slate-700 bg-transparent gap-1 ${u.is_active ? 'text-red-300 hover:bg-red-500/10' : 'text-emerald-300 hover:bg-emerald-500/10'}`}
                                                    onClick={() => toggleActive(u)} disabled={busy} data-testid={`sa-active-${u.user_id}`}>
                                                    <Power className="w-3.5 h-3.5" /> {u.is_active ? 'Disable' : 'Enable'}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Create admin */}
                    <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <UserPlus className="w-4 h-4 text-emerald-400" />
                            <h3 className="text-sm font-semibold">Create admin account</h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                            <Input placeholder="Email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} className="bg-slate-950 border-slate-700" data-testid="sa-create-email" />
                            <Input placeholder="Name" value={cName} onChange={(e) => setCName(e.target.value)} className="bg-slate-950 border-slate-700" data-testid="sa-create-name" />
                            <Input placeholder="Temp password (8+)" value={cPass} onChange={(e) => setCPass(e.target.value)} className="bg-slate-950 border-slate-700" data-testid="sa-create-password" />
                            <Button onClick={createAdmin} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sa-create-btn">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                            </Button>
                        </div>
                        <label className="mt-3 flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                            <input type="checkbox" checked={cSuper} onChange={(e) => setCSuper(e.target.checked)} data-testid="sa-create-super" />
                            Grant super-admin to this new account
                        </label>
                    </div>
                </section>

                {/* Change own password */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <KeyRound className="w-5 h-5 text-emerald-400" />
                        <h2 className="text-base font-semibold">Change My Password</h2>
                    </div>
                    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
                        <Input type="password" placeholder="Current password" value={curPw} onChange={(e) => setCurPw(e.target.value)} className="bg-slate-950 border-slate-700" data-testid="sa-change-current" />
                        <Input type="password" placeholder="New password (8+)" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="bg-slate-950 border-slate-700" data-testid="sa-change-new" />
                        <Button onClick={changeOwnPassword} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sa-change-submit">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update password'}
                        </Button>
                    </div>
                </section>

                {/* Price rollback */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <History className="w-5 h-5 text-emerald-400" />
                        <h2 className="text-base font-semibold">Price Update Rollback</h2>
                    </div>
                    {batches.length === 0 ? (
                        <p className="text-sm text-slate-500" data-testid="sa-no-batches">No bulk price updates recorded yet.</p>
                    ) : (
                        <div className="rounded-xl border border-slate-800 overflow-hidden">
                            <table className="w-full text-sm" data-testid="sa-batches-table">
                                <thead className="bg-slate-900/80 text-slate-400">
                                    <tr className="text-left">
                                        <th className="px-4 py-2 font-medium">Date</th>
                                        <th className="px-4 py-2 font-medium">Products updated</th>
                                        <th className="px-4 py-2 font-medium">By</th>
                                        <th className="px-4 py-2 font-medium text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {batches.map((b) => (
                                        <tr key={b.batch_id} className="border-t border-slate-800" data-testid={`sa-batch-${b.batch_id}`}>
                                            <td className="px-4 py-3 text-slate-300">{fmt(b.timestamp)}</td>
                                            <td className="px-4 py-3">
                                                {b.updated_count}
                                                {b.reverts_batch && <span className="ml-2 text-xs text-amber-300">(rollback)</span>}
                                            </td>
                                            <td className="px-4 py-3 text-slate-400">{b.admin_name || b.admin_email}</td>
                                            <td className="px-4 py-3 text-right">
                                                {!b.reverts_batch && (
                                                    <Button variant="outline" size="sm" className="h-8 border-slate-700 bg-transparent text-amber-300 hover:bg-amber-500/10 gap-1"
                                                        onClick={() => undoBatch(b)} disabled={busy} data-testid={`sa-undo-${b.batch_id}`}>
                                                        <Undo2 className="w-3.5 h-3.5" /> Undo
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </main>

            {/* Reset password dialog */}
            <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setResetPw(''); } }}>
                <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="sa-reset-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-emerald-400" /> Reset password</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Set a new password for <span className="text-slate-200 font-medium">{resetTarget?.email}</span>. They will be signed out of all sessions and must use the new password.
                        </DialogDescription>
                    </DialogHeader>
                    <Input type="text" placeholder="New password (8+ characters)" value={resetPw} onChange={(e) => setResetPw(e.target.value)} className="bg-slate-950 border-slate-700" data-testid="sa-reset-input" />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" className="text-slate-300" onClick={() => { setResetTarget(null); setResetPw(''); }} data-testid="sa-reset-cancel">Cancel</Button>
                        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doReset} disabled={busy} data-testid="sa-reset-confirm">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset password'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
